// ── Tool registry — single source of truth for all tool metadata ──────────────
// Used by: Sidebar, CommandPalette, HomePage, useRecentTools

const SCREENSHOT_ENABLED = import.meta.env.VITE_SCREENSHOT_ENABLED === 'true'

export type ToolCategory =
  | 'Formatters'
  | 'Encoders/Decoders'
  | 'Generators'
  | 'Converters'
  | 'Validators'
  | 'Network Tools'

export interface ToolMeta {
  id:          string
  name:        string
  category:    ToolCategory
  description: string
  route:       string
  isNew?:      boolean
}

export const TOOLS: ToolMeta[] = [
  // ── Formatters ──────────────────────────────────────────────────────────────
  {
    id: 'json-formatter',
    name: 'JSON Formatter',
    category: 'Formatters',
    description: 'Prettify, minify and validate JSON with adjustable indentation',
    route: '/tools/json-formatter',
  },
  {
    id: 'html-formatter',
    name: 'HTML Formatter',
    category: 'Formatters',
    description: 'Format and prettify raw HTML with Prettier',
    route: '/tools/html-formatter',
    isNew: true,
  },
  {
    id: 'sql-formatter',
    name: 'SQL Formatter',
    category: 'Formatters',
    description: 'Format SQL queries for MySQL, PostgreSQL, SQLite and more',
    route: '/tools/sql-formatter',
    isNew: true,
  },
  {
    id: 'markdown-preview',
    name: 'Markdown Preview',
    category: 'Formatters',
    description: 'Write Markdown and preview the rendered HTML output in real time',
    route: '/tools/markdown-preview',
    isNew: true,
  },
  {
    id: 'yaml-json',
    name: 'YAML ↔ JSON',
    category: 'Formatters',
    description: 'Convert between YAML and JSON formats in one click',
    route: '/tools/yaml-json',
    isNew: true,
  },
  // ── Encoders / Decoders ──────────────────────────────────────────────────────
  {
    id: 'base64',
    name: 'Base64 Codec',
    category: 'Encoders/Decoders',
    description: 'Encode text to Base64 or decode Base64 back to plain text',
    route: '/tools/base64',
  },
  {
    id: 'url-encoder',
    name: 'URL Encoder/Decoder',
    category: 'Encoders/Decoders',
    description: 'Percent-encode or decode URLs and query strings',
    route: '/tools/url-encoder',
    isNew: true,
  },
  {
    id: 'html-entities',
    name: 'HTML Entity Encoder',
    category: 'Encoders/Decoders',
    description: 'Encode special characters to HTML entities and decode them back',
    route: '/tools/html-entities',
    isNew: true,
  },
  {
    id: 'jwt-decoder',
    name: 'JWT Decoder',
    category: 'Encoders/Decoders',
    description: 'Decode and inspect JWT header and payload without verification',
    route: '/tools/jwt-decoder',
  },
  // ── Generators ───────────────────────────────────────────────────────────────
  {
    id: 'uuid-generator',
    name: 'UUID Generator',
    category: 'Generators',
    description: 'Generate v1 or v4 UUIDs in bulk',
    route: '/tools/uuid-generator',
  },
  {
    id: 'password-generator',
    name: 'Password Generator',
    category: 'Generators',
    description: 'Generate cryptographically secure passwords with strength meter',
    route: '/tools/password-generator',
    isNew: true,
  },
  {
    id: 'hash-generator',
    name: 'Hash Generator',
    category: 'Generators',
    description: 'Compute MD5, SHA-1, SHA-256 and SHA-512 hashes simultaneously',
    route: '/tools/hash-generator',
    isNew: true,
  },
  {
    id: 'lorem-ipsum',
    name: 'Lorem Ipsum Generator',
    category: 'Generators',
    description: 'Generate placeholder text by paragraphs, sentences or words',
    route: '/tools/lorem-ipsum',
    isNew: true,
  },
  {
    id: 'qr-generator',
    name: 'QR Code Generator',
    category: 'Generators',
    description: 'Generate QR codes as SVG or PNG from any text or URL',
    route: '/tools/qr-generator',
    isNew: true,
  },
  {
    id: 'jwt-generator',
    name: 'JWT Generator',
    category: 'Generators',
    description: 'Sign and create JWT tokens with custom payload and expiry',
    route: '/tools/jwt-generator',
    isNew: true,
  },
  {
    id: 'hmac-generator',
    name: 'HMAC Generator',
    category: 'Generators',
    description: 'Compute HMAC-SHA256/SHA512/MD5 message authentication codes',
    route: '/tools/hmac-generator',
    isNew: true,
  },
  // ── Converters ───────────────────────────────────────────────────────────────
  {
    id: 'timestamp',
    name: 'Timestamp Converter',
    category: 'Converters',
    description: 'Convert Unix timestamps to human-readable dates and vice versa',
    route: '/tools/timestamp',
    isNew: true,
  },
  {
    id: 'color-converter',
    name: 'Color Converter',
    category: 'Converters',
    description: 'Convert between HEX, RGB, HSL and OKLCH colour formats',
    route: '/tools/color-converter',
    isNew: true,
  },
  {
    id: 'number-base',
    name: 'Number Base Converter',
    category: 'Converters',
    description: 'Convert numbers between binary, octal, decimal and hexadecimal',
    route: '/tools/number-base',
    isNew: true,
  },
  {
    id: 'string-case',
    name: 'String Case Converter',
    category: 'Converters',
    description: 'Convert text to camelCase, PascalCase, snake_case, kebab-case and more',
    route: '/tools/string-case',
    isNew: true,
  },
  {
    id: 'csv-json',
    name: 'CSV ↔ JSON',
    category: 'Converters',
    description: 'Convert between CSV and JSON with live DataTable preview',
    route: '/tools/csv-json',
    isNew: true,
  },
  {
    id: 'text-diff',
    name: 'Text Diff Viewer',
    category: 'Converters',
    description: 'Compare two text blocks and visualise differences side by side',
    route: '/tools/text-diff',
    isNew: true,
  },
  {
    id: 'curl-converter',
    name: 'curl → Code',
    category: 'Converters',
    description: 'Convert curl commands to fetch, axios, Python requests or HTTPie',
    route: '/tools/curl-converter',
    isNew: true,
  },
  {
    id: 'toml-json',
    name: 'TOML ↔ JSON',
    category: 'Converters',
    description: 'Convert between TOML and JSON formats',
    route: '/tools/toml-json',
    isNew: true,
  },
  {
    id: 'sql-playground',
    name: 'SQL Playground',
    category: 'Converters',
    description: 'Run SQL queries in the browser with an in-memory SQLite database',
    route: '/tools/sql-playground',
    isNew: true,
  },
  // ── P7 — File Tools (backend file upload) ────────────────────────────────────
  {
    id: 'image-base64',
    name: 'Image → Base64',
    category: 'Encoders/Decoders',
    description: 'Convert any image file to a Base64 data URI for use in CSS or HTML',
    route: '/tools/image-base64',
    isNew: true,
  },
  {
    id: 'svg-optimizer',
    name: 'SVG Optimizer',
    category: 'Converters',
    description: 'Strip metadata and minify SVG files server-side with SVGO',
    route: '/tools/svg-optimizer',
    isNew: true,
  },
  {
    id: 'csv-large',
    name: 'CSV Large File Parser',
    category: 'Converters',
    description: 'Server-side parsing for large CSV files up to 10 MB with paginated preview',
    route: '/tools/csv-large',
    isNew: true,
  },
  {
    id: 'file-hash',
    name: 'File Hash',
    category: 'Generators',
    description: 'Compute MD5, SHA-1, SHA-256 and SHA-512 checksums for any file up to 10 MB',
    route: '/tools/file-hash',
    isNew: true,
  },
  {
    id: 'image-compress',
    name: 'Image Compressor',
    category: 'Generators',
    description: 'Compress JPEG, PNG, WebP images server-side with quality, max-width and format controls',
    route: '/tools/image-compress',
    isNew: true,
  },
  {
    id: 'image-exif',
    name: 'Image EXIF Viewer',
    category: 'Validators',
    description: 'Extract and inspect EXIF metadata from JPEG and PNG images',
    route: '/tools/image-exif',
    isNew: true,
  },
  // ── Validators ───────────────────────────────────────────────────────────────
  {
    id: 'regex-tester',
    name: 'Regex Tester',
    category: 'Validators',
    description: 'Test regular expressions with live match highlighting and group capture',
    route: '/tools/regex-tester',
  },
  {
    id: 'cron-parser',
    name: 'Cron Expression Parser',
    category: 'Validators',
    description: 'Parse cron expressions to English and preview the next 5 run times',
    route: '/tools/cron-parser',
    isNew: true,
  },
  {
    id: 'http-status',
    name: 'HTTP Status Codes',
    category: 'Validators',
    description: 'Searchable reference for all HTTP status codes with RFC links',
    route: '/tools/http-status',
    isNew: true,
  },
  {
    id: 'mime-lookup',
    name: 'MIME Type Lookup',
    category: 'Validators',
    description: 'Look up MIME types by file extension or content type string',
    route: '/tools/mime-lookup',
    isNew: true,
  },
  {
    id: 'json-to-ts',
    name: 'JSON → TypeScript Types',
    category: 'Validators',
    description: 'Generate TypeScript interfaces from any JSON object',
    route: '/tools/json-to-ts',
    isNew: true,
  },
  {
    id: 'python-runner',
    name: 'Python Runner',
    category: 'Validators',
    description: 'Run Python code in your browser via Pyodide — no server, supports numpy and pandas',
    route: '/tools/python-runner',
    isNew: true,
  },
  {
    id: 'js-runner',
    name: 'JavaScript Runner',
    category: 'Validators',
    description: 'Run JavaScript in a secure isolated sandbox — console.log output captured, zero Node globals',
    route: '/tools/js-runner',
    isNew: true,
  },
  {
    id: 'pdf-extractor',
    name: 'PDF Text Extractor',
    category: 'Validators',
    description: 'Extract plain text from PDF files server-side — page count and full-text download',
    route: '/tools/pdf-extractor',
    isNew: true,
  },
  // ── Network Tools ─────────────────────────────────────────────────────────────
  {
    id: 'dns-lookup',
    name: 'DNS Lookup',
    category: 'Network Tools',
    description: 'Query A, AAAA, MX, TXT, NS, CNAME records for any hostname',
    route: '/tools/dns-lookup',
    isNew: true,
  },
  {
    id: 'ssl-inspector',
    name: 'SSL Inspector',
    category: 'Network Tools',
    description: 'Inspect TLS certificate details, expiry, SANs and cipher suite',
    route: '/tools/ssl-inspector',
    isNew: true,
  },
  {
    id: 'http-headers',
    name: 'HTTP Headers Inspector',
    category: 'Network Tools',
    description: 'Fetch and filter response headers from any public URL',
    route: '/tools/http-headers',
    isNew: true,
  },
  {
    id: 'ip-info',
    name: 'IP Info Lookup',
    category: 'Network Tools',
    description: 'Geolocate any IP address — country, region, city, org, ASN',
    route: '/tools/ip-info',
    isNew: true,
  },
  {
    id: 'url-metadata',
    name: 'URL Metadata Inspector',
    category: 'Network Tools',
    description: 'Extract title, description, Open Graph image and canonical URL from any page',
    route: '/tools/url-metadata',
    isNew: true,
  },
  {
    id: 'webhook-tester',
    name: 'Webhook Tester',
    category: 'Network Tools',
    description: 'Create disposable webhook endpoints and inspect incoming HTTP requests — Pro',
    route: '/tools/webhook-tester',
    isNew: true,
  },
  ...(SCREENSHOT_ENABLED ? [{
    id: 'screenshot',
    name: 'Screenshot Tool',
    category: 'Network Tools' as ToolCategory,
    description: 'Capture a full-page screenshot of any public URL — Pro',
    route: '/tools/screenshot',
    isNew: true,
  }] : []),
]

export const TOOLS_BY_ID = Object.fromEntries(TOOLS.map(t => [t.id, t])) as Record<string, ToolMeta>

export const TOOLS_BY_CATEGORY = TOOLS.reduce<Record<ToolCategory, ToolMeta[]>>(
  (acc, tool) => {
    if (!acc[tool.category]) acc[tool.category] = []
    acc[tool.category].push(tool)
    return acc
  },
  {} as Record<ToolCategory, ToolMeta[]>,
)

export const FEATURED_TOOL_IDS: string[] = [
  'html-formatter',
  'sql-formatter',
  'markdown-preview',
  'yaml-json',
  'hash-generator',
  'password-generator',
]
